import { useEffect, useMemo, useState } from "react";

const REGISTRATION_URL = import.meta.env["VITE_OIDC_REGISTRATION_URL"] as string | undefined;

interface Feature {
  emoji: string;
  title: string;
  description: string;
  accent: string;
}

const FEATURES: Feature[] = [
  {
    emoji: "📥",
    title: "Roadmap Inbox",
    description:
      "Committed XI Roadmap entries flow in continuously — signal lineage intact, projected outcomes preserved. Plan it, defer it, or request more context without leaving the surface.",
    accent: "text-cyan-400",
  },
  {
    emoji: "🧭",
    title: "XamunPlanner",
    description:
      "Templated decompose for known archetypes, voice or chat Discovery for novel plays. 24 V1 templates × your workspace's calibration profile produce a draft Play in seconds.",
    accent: "text-emerald-400",
  },
  {
    emoji: "▶️",
    title: "Play Workspace",
    description:
      "Live DAG of steps — decision, comm, query, micro-task, milestone, instrumentation. Operators claim. Agents dispatch. CXO holds the wheel with pause / resume / replan.",
    accent: "text-violet-400",
  },
  {
    emoji: "🤖",
    title: "Agent Fleet",
    description:
      "Microsoft Copilot, Cowork, generic MCP, custom webhooks — bind once, scope per workspace, monitor by capability coverage. BYO Model routes to your provider of choice.",
    accent: "text-blue-400",
  },
  {
    emoji: "📊",
    title: "Outcome Capture",
    description:
      "Pattern 1 agent-pull from instrumentation steps. Pattern 2 software-push via per-play webhook tokens. Achieved versus projected, automatically materialized at play completion.",
    accent: "text-amber-400",
  },
  {
    emoji: "🏛️",
    title: "Governance Insights",
    description:
      "Velocity, accuracy distribution, replan frequency by archetype, decision latency. Peer benchmarks unlock at cohort floor. Channel-A lineage emitted to XI for DTM continuity.",
    accent: "text-fuchsia-400",
  },
];

function buildLoginHref(): string {
  if (typeof window === "undefined") return "/api/auth/login";
  const params = new URLSearchParams(window.location.search);
  const returnTo = params.get("returnTo") ?? params.get("next");
  if (!returnTo) return "/api/auth/login";
  return `/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}

export function LoginPage() {
  const href = useMemo(buildLoginHref, []);
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % FEATURES.length);
    }, 5000);
    return () => clearInterval(t);
  }, []);

  function onSignIn(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    window.location.assign(href);
  }

  const currentFeature = FEATURES[activeFeature]!;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ background: "#fafaf8" }}>
      {/* ─── Left Panel: Value Teaser ──────────────────────────── */}
      <div
        className="hidden lg:flex lg:w-[55%] xl:w-1/2 relative p-10 xl:p-16 overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0a0a1a 0%, #12122a 40%, #1a1035 100%)" }}
      >
        {/* Subtle gradient orbs */}
        <div
          className="absolute top-20 -left-20 w-80 h-80 rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #7c3aed, transparent)" }}
        />
        <div
          className="absolute bottom-20 right-10 w-60 h-60 rounded-full opacity-15 blur-3xl"
          style={{ background: "radial-gradient(circle, #C9A227, transparent)" }}
        />

        <div className="relative z-10 mx-auto w-full max-w-2xl flex flex-col justify-between">
          {/* Top: headline */}
          <div>
            <p
              className="text-[11px] uppercase tracking-[0.22em] mb-4"
              style={{ color: "rgba(201, 162, 39, 0.7)" }}
            >
              Plan · Dispatch · Execute · Learn
            </p>
            <h1 className="text-4xl xl:text-5xl font-extrabold text-white leading-[1.05] mb-5 max-w-xl">
              Strategy execution for the{" "}
              <span
                className="text-transparent bg-clip-text"
                style={{ backgroundImage: "linear-gradient(90deg, #C9A227, #E8D27A)" }}
              >
                AI-orchestrated CXO
              </span>
              .
            </h1>
            <p className="text-gray-300 text-base max-w-md leading-relaxed">
              PlayStudio turns committed Roadmap entries into runnable plays — decomposed by
              XamunPlanner, dispatched across operators and AI agents, governed end-to-end so
              outcomes trace back to intent.
            </p>
          </div>

          {/* Middle: rotating feature cards */}
          <div className="flex-1 flex flex-col justify-center py-10">
            <div
              className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-7 max-w-md mb-6 transition-all duration-500 backdrop-blur-sm"
            >
              <div className="flex items-start gap-5">
                <div
                  className={`w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center flex-shrink-0 text-2xl ${currentFeature.accent}`}
                >
                  {currentFeature.emoji}
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1.5">
                    {currentFeature.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {currentFeature.description}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              {FEATURES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveFeature(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === activeFeature ? "w-8" : "w-1.5 bg-white/20 hover:bg-white/30"
                  }`}
                  style={i === activeFeature ? { backgroundColor: "#C9A227" } : undefined}
                  aria-label={`Show feature ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Bottom: stack signature */}
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.18em] mb-2"
              style={{ color: "#6b7280" }}
            >
              The Xamun stack
            </p>
            <p className="text-xs text-gray-400 leading-relaxed max-w-lg">
              Xamun Intelligence (XI) reads · PlayStudio executes · Software Factory builds.
              Identity federated. Roadmap projected. Outcomes traced back to signal.
            </p>
          </div>
        </div>
      </div>

      {/* ─── Mobile Teaser (shown < lg) ───────────────────────── */}
      <div
        className="lg:hidden px-6 pt-8 pb-4 text-center"
        style={{ background: "linear-gradient(180deg, #12122a, #0a0a0f)" }}
      >
        <img src="/assets/xamun-logo.png" alt="Xamun.AI" className="h-6 mx-auto mb-4" />
        <h2 className="text-lg font-bold text-white mb-1">PlayStudio</h2>
        <p className="text-xs text-gray-400">Strategy execution for CXOs</p>
      </div>

      {/* ─── Right Panel: Login ──────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative overflow-hidden">
        <div
          className="hidden lg:block absolute -top-10 -right-20 w-80 h-80 rounded-full opacity-[0.06] blur-3xl"
          style={{ background: "radial-gradient(circle, #C9A227, transparent)" }}
          aria-hidden
        />
        <div
          className="hidden lg:block absolute -bottom-20 -left-10 w-72 h-72 rounded-full opacity-[0.05] blur-3xl"
          style={{ background: "radial-gradient(circle, #7c3aed, transparent)" }}
          aria-hidden
        />

        <div className="w-full max-w-sm relative z-10">
          <div className="mb-10">
            <img src="/assets/xamun-logo.png" alt="Xamun.AI" className="h-10 mb-8" />
            <h2 className="text-2xl font-bold" style={{ color: "#1a1a2e" }}>
              Sign in
            </h2>
            <p className="text-sm mt-1.5" style={{ color: "#6a6880" }}>
              Continue to your PlayStudio workspace.
            </p>
          </div>

          <div className="space-y-5">
            <a
              href={href}
              onClick={onSignIn}
              className="w-full inline-flex items-center justify-center gap-2.5 rounded-lg py-3 text-base font-semibold text-white transition-all hover:opacity-90"
              style={{ background: "linear-gradient(135deg, #0F9B8E 0%, #5B4FCF 100%)" }}
            >
              <span aria-hidden>→</span>
              Sign in with Xamun.ai
            </a>

            <p className="text-center text-[11px]" style={{ color: "#6a6880" }}>
              You will be redirected to Xamun.ai to sign in securely
            </p>

            {REGISTRATION_URL ? (
              <>
                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" style={{ borderColor: "#e8e6f0" }} />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-3" style={{ background: "#fafaf8", color: "#6a6880" }}>
                      new here?
                    </span>
                  </div>
                </div>

                <a
                  href={REGISTRATION_URL}
                  className="w-full block text-center py-2.5 text-sm font-medium border rounded-lg transition-colors"
                  style={{ borderColor: "#d1d5db", color: "#374151" }}
                >
                  Create a free account
                </a>

                <p className="text-center text-[11px] mt-2" style={{ color: "#9a98a8" }}>
                  Sign up with email or Google — takes 30 seconds
                </p>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
